import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Work } from '@/types/work';

interface BannerCarouselProps {
  items: Work[];
}

export default function BannerCarousel({ items }: BannerCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const count = items.length;

  const goTo = useCallback(
    (next: number) => {
      if (count === 0) return;
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1) return undefined;
    const timer = window.setInterval(() => setIndex((prev) => (prev + 1) % count), 6000);
    return () => window.clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  return (
    <div className="relative w-full overflow-hidden rounded-lg bg-foreground-950">
      <div
        className="flex transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${index * 100}%)` }}
        onTouchStart={(event) => {
          touchStartX.current = event.touches[0].clientX;
        }}
        onTouchEnd={(event) => {
          if (touchStartX.current === null) return;
          const delta = event.changedTouches[0].clientX - touchStartX.current;
          if (Math.abs(delta) > 50) goTo(index + (delta < 0 ? 1 : -1));
          touchStartX.current = null;
        }}
      >
        {items.map((work) => (
          <div key={work.id} className="relative h-[420px] w-full shrink-0 md:h-[560px]">
            <img
              src={work.banner}
              alt={`《${work.title}》焦点剧照`}
              title={`${work.title} 影视焦点推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10" />
            <div className="absolute inset-x-0 bottom-0 flex w-full flex-col gap-3 p-5 pb-14 md:p-10 md:pb-16">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-foreground-950">
                  {work.type === 'tv' ? '热播剧集' : '焦点电影'}
                </span>
                <span className="rounded-md bg-white/15 px-2.5 py-1 text-xs text-background-50 backdrop-blur">
                  {work.rating.toFixed(1)} 分
                </span>
                <span className="rounded-md bg-white/15 px-2.5 py-1 text-xs text-background-50 backdrop-blur">
                  {work.year} · {work.region}
                </span>
              </div>
              <h3 className="text-2xl font-bold text-background-50 md:text-4xl">{work.title}</h3>
              <p className="line-clamp-2 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">{work.summary}</p>
              <div className="mt-1 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${work.id}`}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-circle-line text-base" />
                  查看详情
                </Link>
                <Link
                  to={`/category/${work.type}`}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-white/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-white/15"
                >
                  同类推荐
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => goTo(index - 1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/40 text-background-50 backdrop-blur transition-colors hover:bg-black/60 md:flex md:left-6"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        onClick={() => goTo(index + 1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/40 text-background-50 backdrop-blur transition-colors hover:bg-black/60 md:flex md:right-6"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {items.map((work, dotIndex) => (
          <button
            key={work.id}
            type="button"
            onClick={() => goTo(dotIndex)}
            aria-label={`切换到《${work.title}》`}
            className={`h-2 cursor-pointer rounded-full transition-all ${
              dotIndex === index ? 'w-7 bg-primary-500' : 'w-2 bg-background-50/50 hover:bg-background-50/80'
            }`}
          />
        ))}
      </div>
    </div>
  );
}